import { useState } from 'react';
import { COLORS, SHADOWS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import type { KeycloakUser } from '../api/keycloak-admin';

interface DeleteUserModalProps {
  user: KeycloakUser;
  isDark: boolean;
  colors: ColorTheme;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmation modal for deleting a user */
export function DeleteUserModal({ user, isDark, colors, isDeleting, onConfirm, onCancel }: DeleteUserModalProps): React.JSX.Element {
  const btnTextColor = isDark ? '#D0D0D0' : '#333333';
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim() === user.username;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'grid', placeItems: 'center',
      }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: colors.card,
          borderRadius: '16px',
          boxShadow: isDark ? SHADOWS.cardDark : '0 20px 60px rgba(0,0,0,0.2)',
          padding: '32px',
          width: '420px',
          maxWidth: '90vw',
        }}
      >
        {/* Warning icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%',
            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
            display: 'grid', placeItems: 'center',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>

        <h3 style={{
          fontFamily: FONTS.heading, fontSize: '16px', letterSpacing: '0.1em',
          color: colors.text, textAlign: 'center', margin: '0 0 8px',
          textTransform: 'uppercase',
        }}>
          Delete User
        </h3>
        <p style={{
          fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary,
          textAlign: 'center', margin: '0 0 24px', lineHeight: 1.5,
        }}>
          Deleting <strong style={{ color: colors.text }}>{user.firstName} {user.lastName}</strong> ({user.username}) cannot be undone. Type the username to confirm.
        </p>

        <input
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder={user.username}
          autoFocus
          style={{
            height: '38px', padding: '0 12px', borderRadius: '8px', width: '100%', boxSizing: 'border-box',
            border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
            color: colors.text, fontFamily: FONTS.mono, fontSize: '13px', outline: 'none', marginBottom: '20px',
          }}
        />

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={onCancel}
            style={{
              height: '38px', padding: '0 20px', borderRadius: '8px',
              border: `1px solid ${colors.border}`, backgroundColor: 'transparent',
              color: btnTextColor, fontFamily: FONTS.heading, fontSize: '11px',
              letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting || !confirmed}
            style={{
              height: '38px', padding: '0 20px', borderRadius: '8px',
              border: 'none', backgroundColor: '#EF4444',
              color: '#FFFFFF', fontFamily: FONTS.heading, fontSize: '11px',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              cursor: isDeleting || !confirmed ? 'not-allowed' : 'pointer',
              opacity: isDeleting || !confirmed ? 0.5 : 1,
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete user'}
          </button>
        </div>
      </div>
    </div>
  );
}
